import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Building2, FileText, Mail, UserCog, Users } from 'lucide-react';
import {
  ExcelUploadDialog,
  Skeleton,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger
} from '@bliks-io/ui';
import type { InitializeMembershipRemoval, OrgFunctionKind } from '@bliks-io/contracts';
import { useOrgFunctions } from '@/api/queries/useOrgFunctions';
import { useOrgMembers } from '@/api/queries/useOrgMembers';
import { useDepartments } from '@/api/queries/useDepartments';
import {
  downloadFunctionsTemplate,
  downloadTeamsTemplate,
  type MembershipRowInput,
  type OrgFunctionRowInput,
  type ParsedMembershipRow,
  type ParsedOrgFunctionRow
} from '@/lib/excel';
import {
  createLeadAwareOrgFunctionRowValidator,
  createMemberAwareMembershipRowValidator
} from '../utils/orgFunctionRowValidation';
import {
  EditableDraftTable,
  type DraftColumnDef,
  type DraftSelectOption
} from '../components/EditableDraftTable';
import { membershipDuplicateKeys, orgFunctionDuplicateKeys } from '../utils/draftRowValidation';
import { upsertUploadedRows } from '@/lib/draft-rows/upsertUploadedRows';
import { parseOrgFunctionsForSetup } from '../utils/excelParsers';
import { useSeedOnOpen, type PersonioSeed } from '../useSeedOnOpen';
import { usePersonioImport } from '../usePersonioImport';
import { mergeSetupTeams } from '../utils/mergeSetupTeams';
import { DraftTableToolbar } from '../components/DraftTableToolbar';
import { buildMemberOptions, UserNotFoundBadge } from '../components/memberOptions';
import {
  buildOrgFunctionIdLookup,
  collectRemovedEntityIds,
  collectRemovedFlushedMemberships,
  isMainRoleLockedRow,
  orgFunctionDeleteAction,
  resolveMembershipRemovals
} from '../utils/orgFunctionSurfacePolicies';
import { orgFunctionsToDrafts } from '../utils/existingToDrafts';
import { mergeSurfaceRows } from '../utils/mergeSurfaceRows';
import { clearStaleDuplicateMarks } from '@/lib/draft-rows/markDuplicateRows';
import { normalizedKey } from '@/lib/draft-rows/normalizedKey';
import { departmentOptions, resolveDepartmentToId } from '@/lib/department-ref';
import { DraftSurface } from '../components/DraftSurface';
import {
  explainerFieldsFromColumns,
  explainerIcon,
  type ExplainerSection
} from '../components/FieldExplainer';
import { DraftErrorAlert } from '../components/DraftErrorAlert';
import { ExistingEntitiesTable } from '../components/ExistingEntitiesTable';
import { WizardCallout } from '../components/WizardCallout';
import { WizardStepHeader } from '../components/WizardStepHeader';

export interface OrgFunctionSurfaceDrafts {
  rows: ParsedOrgFunctionRow[];
  memberships: ParsedMembershipRow[];
  /**
   * Ids of persisted OrgFunctions deleted from the table — pending removals
   * the Weiter submit sends (removals never autosave, like the anchor steps).
   */
  removedEntityIds: string[];
  /**
   * Flushed membership pairs deleted from the table, resolved to their link
   * target at queue time — pending unlinks the Weiter submit sends as
   * `membershipRemovals`.
   */
  membershipRemovals: InitializeMembershipRemoval[];
}

interface OrgFunctionSurfaceStepProps {
  kind: OrgFunctionKind;
  drafts: OrgFunctionSurfaceDrafts;
  onDraftsChange: (drafts: OrgFunctionSurfaceDrafts) => void;
  submitError?: string;
  /** Step already imported at least once — re-entry stays additive */
  isDone?: boolean;
  /**
   * Identities (surfaceRowKey) of membership rows the last silent flush
   * persisted — deleting one of these queues a membershipRemovals entry so
   * the Weiter submit unlinks it server-side.
   */
  flushedMembershipKeys?: ReadonlySet<string>;
  /** Personio rows that fill the surface when the step opens empty */
  personioSeed?: PersonioSeed<{
    rows: ParsedOrgFunctionRow[];
    memberships: ParsedMembershipRow[];
  }>;
}

const isPersistedRow = (row: { entityId?: string }) => row.entityId !== undefined;

const EMPTY_KEY_SET: ReadonlySet<string> = new Set();

// Every field the Excel template carries stays editable here (deviation
// from the design, which hides Abteilung on Teams and Shared-Postfach on
// Funktionen): a hidden column would make an invalid imported value
// uncorrectable and submit data the user cannot see.
function buildRowColumns(
  kind: OrgFunctionKind,
  departmentOptions: DraftSelectOption[],
  memberOptions: DraftSelectOption[]
): DraftColumnDef<OrgFunctionRowInput>[] {
  const columnArea = kind === 'role' ? 'functions' : 'teams';
  return [
    {
      field: 'name',
      headerKey: `bulkOnboarding.excel.columns.${columnArea}.name`,
      descriptionKey: 'initialSetup.columnHints.functions.name',
      required: true,
      explainer: {
        icon: explainerIcon(Users),
        descriptionKey: 'initialSetup.fieldExplainer.fields.functions.name'
      }
    },
    {
      field: 'description',
      headerKey: `bulkOnboarding.excel.columns.${columnArea}.description`,
      descriptionKey: 'initialSetup.columnHints.functions.description',
      optional: true,
      widthClass: 'w-[28%]',
      explainer: {
        icon: explainerIcon(FileText),
        descriptionKey: 'initialSetup.fieldExplainer.fields.functions.description'
      }
    },
    {
      field: 'department',
      headerKey: `bulkOnboarding.excel.columns.${columnArea}.department`,
      descriptionKey: 'initialSetup.columnHints.functions.department',
      options: departmentOptions,
      optional: true,
      explainer: {
        icon: explainerIcon(Building2),
        descriptionKey: 'initialSetup.fieldExplainer.fields.functions.department'
      }
    },
    {
      field: 'leadEmail',
      headerKey: 'initialSetup.functionsTeams.columns.lead',
      descriptionKey: 'initialSetup.columnHints.functions.lead',
      options: memberOptions,
      unknownValueContent: <UserNotFoundBadge />,
      optional: true,
      explainer: {
        icon: explainerIcon(UserCog),
        descriptionKey: 'initialSetup.fieldExplainer.fields.functions.lead'
      }
    },
    {
      field: 'email',
      headerKey: 'initialSetup.functionsTeams.columns.sharedMailbox',
      descriptionKey: 'initialSetup.columnHints.functions.sharedMailbox',
      optional: true,
      cellClassName: 'font-mono text-xs text-fg-secondary',
      explainer: {
        icon: explainerIcon(Mail),
        descriptionKey: 'initialSetup.fieldExplainer.fields.functions.sharedMailbox'
      }
    }
  ];
}

function buildMembershipColumns(
  kind: OrgFunctionKind,
  memberOptions: DraftSelectOption[],
  targetOptions: DraftSelectOption[]
): DraftColumnDef<MembershipRowInput>[] {
  return [
    {
      field: 'memberEmail',
      headerKey: 'initialSetup.functionsTeams.columns.member',
      descriptionKey: 'initialSetup.columnHints.memberships.member',
      options: memberOptions,
      unknownValueContent: <UserNotFoundBadge />,
      required: true,
      explainer: {
        icon: explainerIcon(Mail),
        descriptionKey: 'initialSetup.fieldExplainer.fields.memberships.member'
      }
    },
    {
      field: 'orgFunctionName',
      headerKey:
        kind === 'role'
          ? 'initialSetup.functionsTeams.columns.function'
          : 'initialSetup.functionsTeams.columns.team',
      descriptionKey: 'initialSetup.columnHints.memberships.target',
      options: targetOptions,
      required: true,
      explainer: {
        icon: explainerIcon(Users),
        descriptionKey: 'initialSetup.fieldExplainer.fields.memberships.target'
      }
    }
  ];
}

// Membership targets resolve against the displayed batch plus the
// OrgFunctions of this kind that already exist.
function buildTargetOptions(
  rows: ParsedOrgFunctionRow[],
  existingOrgFunctions: { name: string }[]
): DraftSelectOption[] {
  const rowNames = new Set(rows.map((row) => row.data.name).filter(Boolean));
  return [
    ...[...rowNames].map((name) => ({ value: name, label: name })),
    ...existingOrgFunctions
      .filter((fn) => !rowNames.has(fn.name))
      .map((fn) => ({ value: fn.name, label: fn.name }))
  ];
}

type SurfaceTab = 'entities' | 'memberships';

// Funktionen auto-minted from Stellenbezeichnungen don't count as content:
// until the user adds something of their own, the step keeps teaching the
// template via the explainer empty state — the derived ones are already
// announced by the autoCreatedBanner. For the Teams step mainRoleFunctionIds
// is empty, so this reduces to "no drafts and nothing existing". Uploads and
// manual adds stay safe: mergeSurfaceRows folds the derived seeds back into
// the table the moment the draft owns it.
function isSurfaceEmpty(
  drafts: OrgFunctionSurfaceDrafts,
  existingRows: ParsedOrgFunctionRow[],
  mainRoleFunctionIds: Set<string>
): boolean {
  const isDraftUntouched =
    drafts.rows.length === 0 &&
    drafts.memberships.length === 0 &&
    drafts.removedEntityIds.length === 0 &&
    drafts.membershipRemovals.length === 0;
  const hasOnlyDerivedExisting = existingRows.every(
    (row) => row.entityId && mainRoleFunctionIds.has(row.entityId)
  );
  return isDraftUntouched && hasOnlyDerivedExisting;
}

/**
 * Row count in the table-header pill style plus a red pill for invalid rows —
 * after an Excel import both tabs show at a glance how much landed where and
 * whether anything behind the inactive tab needs fixing.
 */
function TabCountBadges({ count, invalidCount }: { count: number; invalidCount: number }) {
  return (
    <>
      <span className="ml-2 rounded-full bg-muted px-2 py-0.5 font-mono text-2xs font-semibold text-fg-secondary">
        {count}
      </span>
      {invalidCount > 0 && (
        <span className="ml-1.5 rounded-full bg-danger-solid/10 px-1.5 py-px text-[10.5px] font-bold text-danger">
          {invalidCount}
        </span>
      )}
    </>
  );
}

// Underline-style restyle of the segmented ui Tabs default, kept identical to
// the wizard's other tab bars.
const TAB_LIST_CLASS =
  'mb-5 flex w-full justify-start rounded-none border-b border-line bg-transparent p-0';
const TAB_TRIGGER_CLASS =
  '-mb-px mr-6 flex items-center rounded-none border-b-2 border-transparent bg-transparent px-0 pb-2.5 pt-0 text-sm font-semibold text-fg-secondary shadow-none hover:text-foreground data-[state=active]:border-bliks-green-500 data-[state=active]:bg-transparent data-[state=active]:text-foreground data-[state=active]:shadow-none';

/**
 * One kind-fixed wizard step (ADR 0004): the Funktionen step only ever
 * creates role-kind, the Teams step team-kind — templates, upload, and
 * manually added draft rows all inherit the kind from the step.
 */
export function OrgFunctionSurfaceStep({
  kind,
  drafts,
  onDraftsChange,
  submitError,
  isDone = false,
  flushedMembershipKeys = EMPTY_KEY_SET,
  personioSeed
}: OrgFunctionSurfaceStepProps) {
  const { t } = useTranslation();
  const sectionKey = kind === 'role' ? 'functionsSection' : 'teamsSection';
  const [isUploadOpen, setIsUploadOpen] = useState(false);
  const [activeTab, setActiveTab] = useState<SurfaceTab>('entities');
  const { data: existingOrgFunctions, isPending: isOrgFunctionsPending } = useOrgFunctions(kind);
  const { data: departments } = useDepartments();
  // ALL statuses on purpose: the backend resolves lead/membership emails and
  // blocks removals of held Hauptfunktionen for users of EVERY status — a
  // suspended member's Stelle must still lock its Funktion here, or the
  // enabled delete queues a removal the server rejects. Internal-only stays:
  // externals never hold OrgFunctions (reclassification unsets both slots).
  const { data: orgMembers, isPending: isOrgMembersPending } = useOrgMembers({
    classification: 'internal'
  });
  // The empty-vs-table decision below reads both queries; deciding before they
  // resolve would flash the explainer empty state over a seeded table.
  const isSeedDataPending = isOrgFunctionsPending || isOrgMembersPending;

  const departmentSelectOptions: DraftSelectOption[] = useMemo(
    () => departmentOptions(departments ?? []),
    [departments]
  );
  // buildMemberOptions constructs a <PersonChip> element per member — memoize
  // so a keystroke into any cell does not rebuild every option element.
  const memberOptions = useMemo(() => buildMemberOptions(orgMembers), [orgMembers]);

  // Leitung/membership emails must resolve to an internal member — flag them
  // in the table instead of failing at submit.
  const memberEmails = useMemo(
    () => new Set((orgMembers ?? []).map((member) => member.email.toLowerCase())),
    [orgMembers]
  );
  const isMember = (email: string) => memberEmails.has(email.toLowerCase());

  // Role Funktionen that are a member's Stelle (mainRole) must not be deletable
  // here — removing one would orphan that person's position. Teams carry no
  // such link, so the set stays empty for the Teams step.
  const mainRoleFunctionIds = useMemo(() => {
    if (kind !== 'role') return new Set<string>();
    return new Set(
      (orgMembers ?? [])
        .map((member) => member.mainRole?._id)
        .filter((id): id is string => Boolean(id))
    );
  }, [kind, orgMembers]);
  const validateOrgFunctionRow = useMemo(
    () => createLeadAwareOrgFunctionRowValidator(memberEmails),
    [memberEmails]
  );
  const validateMembershipRow = useMemo(
    () => createMemberAwareMembershipRowValidator(memberEmails),
    [memberEmails]
  );

  // What already exists (e.g. Funktionen auto-minted from the users import's
  // Stellenbezeichnungen) is part of the ONE table, not a separate list.
  // Untouched, the seeds live only in the render; the first edit or upload
  // captures them into the draft state. Re-submitting them is idempotent —
  // the import skips existing rows by (name, kind).
  // Rows deleted from the table are pending removals until Weiter commits
  // them — a seed queued for removal must not resurface via the seed
  // fallback or the display merge in the meantime.
  const existingRows: ParsedOrgFunctionRow[] = useMemo(() => {
    const removedIds = new Set(drafts.removedEntityIds);
    return orgFunctionsToDrafts(existingOrgFunctions ?? []).filter(
      (row) => !row.entityId || !removedIds.has(row.entityId)
    );
  }, [existingOrgFunctions, drafts.removedEntityIds]);
  // Untouched, the seeds render directly. Once the draft owns the table, merge
  // in server roles minted elsewhere (e.g. a Stelle added on the Mitarbeiter
  // step) without clobbering local edits — so a cross-step change shows up
  // without a page refresh. Draft rows carry the ids of functions autosave
  // created silently (stamped from the flush response), so the id-based merge
  // recognizes their refetched server copies; the name key covers the frame
  // before the stamp lands.
  const rows = useMemo(
    () =>
      drafts.rows.length === 0
        ? existingRows
        : mergeSurfaceRows(drafts.rows, existingRows, (data: OrgFunctionRowInput) =>
            normalizedKey(data.name)
          ),
    [drafts.rows, existingRows]
  );

  // A repeat import updates matching on-screen rows (drafts or seeds) in
  // place instead of stacking duplicates.
  const mergeImported = (
    importedRows: ParsedOrgFunctionRow[],
    importedMemberships: ParsedMembershipRow[]
  ) => {
    onDraftsChange({
      ...drafts,
      rows: upsertUploadedRows(rows, importedRows, orgFunctionDuplicateKeys),
      memberships: upsertUploadedRows(
        drafts.memberships,
        importedMemberships,
        membershipDuplicateKeys
      )
    });
    return importedRows.length + importedMemberships.length;
  };

  // A lead or member the users step did not create would be a blocking row.
  const mergePersonioTeams = (imported: Pick<OrgFunctionSurfaceDrafts, 'rows' | 'memberships'>) => {
    const memberships = imported.memberships.filter((row) => isMember(row.data.memberEmail));
    const merged = mergeSetupTeams(
      { rows, memberships: drafts.memberships },
      {
        rows: imported.rows.map((row) =>
          !row.data.leadEmail || isMember(row.data.leadEmail)
            ? row
            : { ...row, data: { ...row.data, leadEmail: undefined } }
        ),
        memberships
      }
    );
    if (merged.rows !== rows || merged.memberships !== drafts.memberships) {
      onDraftsChange({ ...drafts, ...merged });
    }
    return imported.rows.length + memberships.length;
  };

  const handleParse = async (file: File) => {
    const result = await parseOrgFunctionsForSetup(file, kind);
    // Resolve the sheet's department abbreviation to the stable id at the import
    // boundary.
    const resolvedRows = result.rows.map((row) => ({
      ...row,
      data: {
        ...row.data,
        // Optional on functions/teams — leave an empty cell untouched.
        department: row.data.department
          ? resolveDepartmentToId(row.data.department, departments ?? [])
          : row.data.department
      }
    }));
    // Personio's employee export merges like a Personio read.
    const imported = result.personioExport
      ? mergePersonioTeams(result)
      : mergeImported(resolvedRows, result.memberships);
    if (imported > 0) personioSeed?.onReplaced();
    return imported;
  };

  // Table changes arrive as the full next row set; persisted rows that
  // vanished from it were deleted by the user and queue as pending removals
  // (committed on Weiter — removals never autosave). Edits keep their id and
  // queue nothing.
  // An emptied surface no longer shows Personio rows, whatever comes next.
  const markReplacedWhenEmptied = (
    nextRows: ParsedOrgFunctionRow[],
    nextMemberships: ParsedMembershipRow[]
  ) => {
    const hasRows = nextRows.some((row) => !isMainRoleLockedRow(row, mainRoleFunctionIds));
    if (!hasRows && nextMemberships.length === 0) personioSeed?.onReplaced();
  };

  const handleEntityRowsChange = (nextRows: ParsedOrgFunctionRow[]) => {
    markReplacedWhenEmptied(nextRows, drafts.memberships);
    const removedIds = collectRemovedEntityIds(rows, nextRows);
    onDraftsChange({
      ...drafts,
      rows: nextRows,
      removedEntityIds:
        removedIds.length > 0
          ? [...drafts.removedEntityIds, ...removedIds]
          : drafts.removedEntityIds
    });
  };

  // Locked Stelle-derived rows survive the clear and may lose their twin.
  const clearUnlockedEntityRows = () => {
    handleEntityRowsChange(
      clearStaleDuplicateMarks(
        rows.filter((row) => isMainRoleLockedRow(row, mainRoleFunctionIds)),
        orgFunctionDuplicateKeys
      )
    );
    personioSeed?.onReplaced();
  };

  const handleMembershipRowsChange = (nextMemberships: ParsedMembershipRow[]) => {
    markReplacedWhenEmptied(rows, nextMemberships);
    const removedFlushed = collectRemovedFlushedMemberships(
      drafts.memberships,
      nextMemberships,
      flushedMembershipKeys
    );
    const removals = resolveMembershipRemovals(
      removedFlushed,
      buildOrgFunctionIdLookup(rows, existingOrgFunctions ?? [])
    );
    onDraftsChange({
      ...drafts,
      memberships: nextMemberships,
      membershipRemovals:
        removals.length > 0
          ? [...drafts.membershipRemovals, ...removals]
          : drafts.membershipRemovals
    });
  };

  const rowColumns = buildRowColumns(kind, departmentSelectOptions, memberOptions);
  const targetOptions = useMemo(() => {
    const removedIds = new Set(drafts.removedEntityIds);
    return buildTargetOptions(
      rows,
      (existingOrgFunctions ?? []).filter((fn) => !removedIds.has(fn._id))
    );
  }, [rows, existingOrgFunctions, drafts.removedEntityIds]);
  const membershipColumns = buildMembershipColumns(kind, memberOptions, targetOptions);

  const sections: ExplainerSection[] = [
    {
      titleKey: 'initialSetup.fieldExplainer.sections.entity',
      fields: explainerFieldsFromColumns(rowColumns)
    },
    {
      titleKey: 'initialSetup.fieldExplainer.sections.memberships',
      fields: explainerFieldsFromColumns(membershipColumns)
    }
  ];
  const downloadTemplate = kind === 'role' ? downloadFunctionsTemplate : downloadTeamsTemplate;
  const addRowLabelKey =
    kind === 'role' ? 'initialSetup.addRow.orgFunction' : 'initialSetup.addRow.team';

  const invalidEntityCount = rows.filter((row) => !row.isValid).length;
  const invalidMembershipCount = drafts.memberships.filter((row) => !row.isValid).length;
  const invalidRowCount = invalidEntityCount + invalidMembershipCount;
  const surfaceEmpty = isSurfaceEmpty(drafts, existingRows, mainRoleFunctionIds);

  const personioImport = usePersonioImport({ onLoaded: personioSeed?.onLoaded });
  const personioButton = kind === 'team' && personioImport.toolbarButton;

  // Seeds only into a surface the admin sees, so autosave only ever saves rows
  // shown here. On open only an empty surface of an open step takes the rows;
  // the step's own load merges into any surface.
  useSeedOnOpen({
    personioSeed,
    ready: !isSeedDataPending,
    seed: ({ rows: seedRows, memberships }) => {
      if (seedRows.length + memberships.length === 0) return false;
      if (!personioSeed?.ownLoad && (isDone || !surfaceEmpty)) return false;
      mergePersonioTeams({ rows: seedRows, memberships });
      return true;
    }
  });

  // The banner announces only the Funktionen auto-minted from the users
  // import's Stellenbezeichnungen — not every pre-existing role.
  const derivedExistingCount = existingRows.filter(
    (row) => row.entityId && mainRoleFunctionIds.has(row.entityId)
  ).length;

  // Stelle-derived Funktionen get a subtle background tint so their special
  // provenance stays visible in both the seeded and the editable table.
  const derivedRowClassName = (row: ParsedOrgFunctionRow) =>
    row.entityId && mainRoleFunctionIds.has(row.entityId) ? 'bg-surface-subtle' : undefined;

  const makeEmptyOrgFunctionRow = (rowNumber: number): ParsedOrgFunctionRow => {
    const data: OrgFunctionRowInput = { name: '' };
    return { data, isValid: true, rowNumber };
  };

  return (
    <div className="space-y-6 px-10 pb-6 pt-9">
      <WizardStepHeader
        title={t(`initialSetup.functionsTeams.${sectionKey}.stepTitle`)}
        description={t(
          personioSeed?.loaded && !surfaceEmpty
            ? 'initialSetup.personio.loaded'
            : `initialSetup.functionsTeams.${sectionKey}.description`
        )}
        hint={t(`initialSetup.functionsTeams.${sectionKey}.hint`)}
        isDone={isDone}
      />

      {kind === 'role' && derivedExistingCount > 0 && (
        <WizardCallout variant="hint">
          {t('initialSetup.functionsTeams.autoCreatedBanner', { count: derivedExistingCount })}
        </WizardCallout>
      )}

      <DraftErrorAlert submitError={submitError} invalidRowCount={invalidRowCount} />

      {isSeedDataPending && (
        <div className="space-y-3">
          <Skeleton className="h-10 w-full" />
          <Skeleton className="h-40 w-full" />
        </div>
      )}
      {!isSeedDataPending && (
        <DraftSurface
          isEmpty={surfaceEmpty}
          headingKey="initialSetup.fieldExplainer.headings.functionsTeams"
          sections={sections}
          downloadTemplate={downloadTemplate}
          parse={handleParse}
          onAddManually={() => onDraftsChange({ ...drafts, rows: [makeEmptyOrgFunctionRow(1)] })}
          addLabelKey={addRowLabelKey}
          importActions={personioButton}
          emptyExtras={
            // The Stelle-derived Funktionen stay visible under the explainer;
            // they become editable rows once the table takes over.
            <ExistingEntitiesTable
              rows={existingRows}
              columns={rowColumns}
              rowClassName={derivedRowClassName}
            />
          }
        >
          <Tabs value={activeTab} onValueChange={(value) => setActiveTab(value as SurfaceTab)}>
            <TabsList className={TAB_LIST_CLASS}>
              <TabsTrigger value="entities" className={TAB_TRIGGER_CLASS}>
                {t(`initialSetup.functionsTeams.${sectionKey}.title`)}
                <TabCountBadges count={rows.length} invalidCount={invalidEntityCount} />
              </TabsTrigger>
              <TabsTrigger value="memberships" className={TAB_TRIGGER_CLASS}>
                {t('initialSetup.functionsTeams.membershipsTable.heading')}
                <TabCountBadges
                  count={drafts.memberships.length}
                  invalidCount={invalidMembershipCount}
                />
              </TabsTrigger>
            </TabsList>

            <TabsContent value="entities" className="mt-0">
              <EditableDraftTable
                rows={rows}
                onRowsChange={handleEntityRowsChange}
                columns={rowColumns}
                validateRow={validateOrgFunctionRow}
                duplicateChecks={orgFunctionDuplicateKeys}
                addLabelKey={addRowLabelKey}
                makeEmptyRow={makeEmptyOrgFunctionRow}
                titleKey={`initialSetup.functionsTeams.${sectionKey}.title`}
                // Persisted rows delete via the removals wire; only a Funktion
                // that is someone's Hauptfunktion stays locked — deleting it
                // would orphan that person's Stelle.
                deleteAction={(row) => orgFunctionDeleteAction(row, mainRoleFunctionIds)}
                deleteDisabledTitle={t('initialSetup.functionsTeams.mainRoleLockedRemove')}
                rowClassName={derivedRowClassName}
                toolbar={
                  <DraftTableToolbar
                    canClear={rows.some((row) => !isMainRoleLockedRow(row, mainRoleFunctionIds))}
                    onClear={clearUnlockedEntityRows}
                    onUpload={() => setIsUploadOpen(true)}
                    importActions={personioButton}
                    clearScope={
                      rows.some(
                        (row) =>
                          isPersistedRow(row) && !isMainRoleLockedRow(row, mainRoleFunctionIds)
                      )
                        ? 'all'
                        : 'drafts'
                    }
                  />
                }
              />
              {kind === 'role' && (
                <div className="mt-5 flex max-w-[82ch] items-start gap-2.5 text-[12.5px] leading-[1.55] text-fg-secondary">
                  <Mail className="mt-px h-[15px] w-[15px] shrink-0 text-fg-subtle" />
                  {t('initialSetup.functionsTeams.mailImportNote')}
                </div>
              )}
            </TabsContent>

            <TabsContent value="memberships" className="mt-0">
              <EditableDraftTable
                rows={drafts.memberships}
                onRowsChange={handleMembershipRowsChange}
                columns={membershipColumns}
                validateRow={validateMembershipRow}
                duplicateChecks={membershipDuplicateKeys}
                addLabelKey="initialSetup.addRow.membership"
                makeEmptyRow={(rowNumber) => {
                  const data: MembershipRowInput = { orgFunctionName: '', memberEmail: '' };
                  return { data, isValid: true, rowNumber };
                }}
                titleKey="initialSetup.functionsTeams.membershipsTable.heading"
                toolbar={
                  <DraftTableToolbar
                    canClear={drafts.memberships.length > 0}
                    onClear={() => handleMembershipRowsChange([])}
                    onUpload={() => setIsUploadOpen(true)}
                    clearScope={flushedMembershipKeys.size > 0 ? 'all' : 'drafts'}
                  />
                }
              />
            </TabsContent>
          </Tabs>
        </DraftSurface>
      )}

      <ExcelUploadDialog
        open={isUploadOpen}
        onOpenChange={setIsUploadOpen}
        title={t(`initialSetup.functionsTeams.${sectionKey}.title`)}
        downloadTemplate={downloadTemplate}
        parse={handleParse}
      />
    </div>
  );
}
